import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { useSelector } from 'react-redux';
import { Skeleton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faChevronRight, faHandshake, faTruckFast } from '@fortawesome/free-solid-svg-icons';
import { RootState } from 'store';
import { AvatarWithFlag, normalizeCountryCode } from 'components/AvatarWithFlag/AvatarWithFlag';
import BadgeIcon from 'components/BadgeIcon/BadgeIcon';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import { TIER_FROM_LEVEL } from 'types/interfaces/badge';
import { UserHoverCardData } from 'types/interfaces/hoverCard';
import { HoverCardPosition } from 'hooks/useHoverCard';
import { useAverageImageColor } from 'hooks/useAverageImageColor';
import { formatLocation } from 'utils/formatLocation';
import './UserHoverCard.css';

/**
 * The trader card (Asana 1219748562973072): name bar, art box, type line with
 * the flag as the set symbol, text box, Sent/Received in a power/toughness box.
 * Elite is the foil printing: profile art, tints and a frame in the user's own
 * border colours, a sheen sweep, room for 8 badges.
 */
interface UserHoverCardProps {
  data?: UserHoverCardData;
  isLoading: boolean;
  position: HoverCardPosition;
  cardProps: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
  /**
   * Render in place (the banner picker's preview): no portal, no fixed
   * positioning, cardProps ignored. Every other style and animation is unchanged.
   */
  inline?: boolean;
}

const DEFAULT_C1 = '#FFD700';
const DEFAULT_C2 = '#9B59B6';
/** The ticket asks for the U+2014 character between the rank and the location. */
const TYPE_LINE_SEPARATOR = ' — ';

const SKELETON_SX = { bgcolor: 'rgba(255,255,255,0.06)' };

const LoadingSkeleton: React.FC = () => (
  <div className="user-hover-card__panel user-hover-card__panel--skeleton">
    <Skeleton variant="rectangular" height={40} sx={SKELETON_SX} />
    <Skeleton variant="rectangular" height={132} sx={SKELETON_SX} />
    <Skeleton variant="rectangular" height={32} sx={SKELETON_SX} />
    <div className="user-hover-card__skeleton-lines">
      <Skeleton variant="text" width="90%" height={14} sx={SKELETON_SX} />
      <Skeleton variant="text" width="75%" height={14} sx={SKELETON_SX} />
      <Skeleton variant="text" width="60%" height={14} sx={SKELETON_SX} />
    </div>
  </div>
);

const CardBody: React.FC<{ data: UserHoverCardData }> = ({ data }) => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isElite = !!data.isElite;
  const animated = isElite && !!data.showAnimatedBorder;
  const isViewerSelf = currentUser?.id === data.id;
  const showUpsell = isViewerSelf && !isElite;

  const averageColor = useAverageImageColor(isElite ? null : data.avatarUrl);
  // Elite art box (Asana 1219946182379030): the chosen banner, or a gradient in
  // the member's own border colours. Never the avatar art twice.
  const bannerUrl = isElite && data.bannerUrl ? data.bannerUrl : null;
  const location = formatLocation(data.city, data.state);
  const countryCode = data.country ? normalizeCountryCode(data.country) : null;
  const showRating = data.averageRating !== null && data.averageRating !== undefined && data.totalRatings > 0;
  const badges = data.displayedBadges ?? [];

  return (
    <div className="user-hover-card__panel">
      <div className="user-hover-card__name-bar">
        <span className="user-hover-card__name">
          {data.username}
          {isElite && <EliteBadge isElite size={16} className="user-hover-card__elite-badge" />}
        </span>
        {showRating && (
          <span className="user-hover-card__rating-pill" data-testid="hover-card-rating">
            <span className="user-hover-card__rating-value">&#9733; {data.averageRating!.toFixed(1)}</span>
            <span className="user-hover-card__rating-count">({data.totalRatings})</span>
          </span>
        )}
      </div>

      <div
        className="user-hover-card__art"
        style={{ '--hc-avg': averageColor } as React.CSSProperties}
        data-testid="hover-card-art"
      >
        {bannerUrl && (
          <img className="user-hover-card__art-img" src={bannerUrl} alt="" aria-hidden="true" data-testid="hover-card-banner" />
        )}
        {isElite && !bannerUrl && (
          <div className="user-hover-card__art-gradient" aria-hidden="true" data-testid="hover-card-art-gradient" />
        )}
        {isElite && <div className="user-hover-card__vignette" aria-hidden="true" />}
        <div className="user-hover-card__avatar">
          <AvatarWithFlag
            avatarUrl={data.avatarUrl}
            username={data.username}
            size={isElite ? 96 : 92}
            countryCode={null}
            isElite={isElite}
            showAnimatedBorder={animated}
            borderColor1={data.borderColor1}
            borderColor2={data.borderColor2}
          />
        </div>
      </div>

      <div className="user-hover-card__type-line">
        <span className="user-hover-card__type-text">
          {isElite ? 'Elite Trader' : 'Trader'}
          {location ? `${TYPE_LINE_SEPARATOR}${location}` : ''}
        </span>
        {countryCode && (
          <img
            className="user-hover-card__flag"
            src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${countryCode}.svg`}
            alt={countryCode}
            data-testid="hover-card-flag"
          />
        )}
      </div>

      <div className="user-hover-card__text-box">
        {data.bio && <p className="user-hover-card__bio">{data.bio}</p>}
        <div className="user-hover-card__meta">
          <span className="user-hover-card__meta-item">
            <FontAwesomeIcon icon={faHandshake} className="user-hover-card__meta-icon" />
            <strong>{data.uniqueTradePartners}</strong> partners
          </span>
          {data.avgShipTimeDays !== null && data.avgShipTimeDays !== undefined && (
            <span className="user-hover-card__meta-item" data-testid="hover-card-ship-time">
              <FontAwesomeIcon icon={faTruckFast} className="user-hover-card__meta-icon" />
              Ships in <strong>{data.avgShipTimeDays.toFixed(1)}d</strong>
            </span>
          )}
        </div>
        {badges.length > 0 && (
          <div className="user-hover-card__shelf" data-testid="hover-card-badges">
            {badges.map((badge) => {
              const tier = TIER_FROM_LEVEL[badge.level] || 'bronze';
              return (
                <div key={badge.slug} className="user-hover-card__badge-item">
                  <BadgeIcon icon={badge.icon} shape={badge.shape} tier={tier} size={30} earned />
                  <span className="user-hover-card__badge-title">{badge.title}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="user-hover-card__footer">
        <Link to={`/profile/u/${encodeURIComponent(data.username)}`} className="user-hover-card__profile-link">
          View Profile <FontAwesomeIcon icon={faArrowRight} className="user-hover-card__link-icon" />
        </Link>
        <div className="user-hover-card__pt">
          <span className="user-hover-card__pt-value">
            {data.cardsSent}/{data.cardsReceived}
          </span>
          <span className="user-hover-card__pt-label">SENT / RECV</span>
        </div>
      </div>

      {showUpsell && (
        <Link to="/elite?ref=hover-card" className="user-hover-card__upsell" data-testid="hover-card-upsell">
          <EliteBadge isElite size={14} />
          <span className="user-hover-card__upsell-text">
            Get the foil version with <span className="user-hover-card__upsell-elite">Elite</span>
          </span>
          <FontAwesomeIcon icon={faChevronRight} className="user-hover-card__upsell-chevron" />
        </Link>
      )}

      {animated && (
        <div className="user-hover-card__sheen" aria-hidden="true" data-testid="hover-card-sheen">
          <div className="user-hover-card__sheen-band" />
        </div>
      )}
    </div>
  );
};

const UserHoverCard: React.FC<UserHoverCardProps> = ({ data, isLoading, position, cardProps, inline = false }) => {
  const isElite = !!data?.isElite;
  const animated = isElite && !!data?.showAnimatedBorder;
  const style = {
    ...(inline ? {} : { left: position.left, top: position.top }),
    '--hc-c1': data?.borderColor1 || DEFAULT_C1,
    '--hc-c2': data?.borderColor2 || DEFAULT_C2,
  } as React.CSSProperties;

  const content = (
    <div
      className={`user-hover-card${isElite ? ' user-hover-card--elite' : ''}${animated ? ' user-hover-card--animated' : ''}${inline ? ' user-hover-card--inline' : ''}`}
      style={style}
      data-testid="user-hover-card"
      {...(inline ? {} : cardProps)}
    >
      {isElite && !isLoading && data && <div className="user-hover-card__frame" aria-hidden="true" data-testid="hover-card-frame" />}
      {isLoading || !data ? <LoadingSkeleton /> : <CardBody data={data} />}
    </div>
  );

  if (inline) return content;
  return createPortal(content, document.body);
};

export default UserHoverCard;
